import { useState, useEffect } from "react";
import { Popover, Input, Button, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import checkIcon from "src/assets/svgs/icon-check-white.svg";
import addIcon from "src/assets/svgs/icon-add.svg";
import styles from "./ArmAndCohortSelectorPopover.module.scss";

interface ArmAndCohortSelectorProps {
  category: "Arm" | "Cohort";
  table_id?: number;
  allOptions: Digital_Protocol_Arm_Types.Option[];
  selected: {
    arms: Digital_Protocol_Arm_Types.Option[];
    cohorts: Digital_Protocol_Arm_Types.Option[];
  };
  errMsg?: string;
  doConfirm: (
    payload:
      | Digital_Protocol_Arm_Cohort_Mapping.Payload
      | Digital_Protocol_Arm_Cohort_Mapping.Option[]
  ) => any;
  onVisibleChange?: (v: boolean, type: "Arm" | "Cohort", tableId?: number) => void;
}

const SELECT_LIMIT = 10;
const COHORT_EXCEED_MSG =
  "Cohort limit exceeded. You can select a maximum of 10 cohorts.";

export function ArmAndCohortSelectorPopover({
  category,
  table_id,
  allOptions,
  selected,
  errMsg,
  doConfirm,
  onVisibleChange
}: ArmAndCohortSelectorProps) {
  const [visible, setVisible] = useState(false);
  const [searchTxt, setSearchTxt] = useState<string>("");
  const [newSelected, setNewSelected] = useState<
    Digital_Protocol_Arm_Types.Option[]
  >(category === "Arm" ? selected.arms : selected.cohorts);
  const [filteredAllOptions, setFilteredAllOptions] = useState<
    Digital_Protocol_Arm_Types.Option[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);


  const openPopover = () => {
    if (onVisibleChange) {
      onVisibleChange(true, category, table_id);
    } else {
      setVisible(true);
    }
  };

  const closePopover = () => {
    if (onVisibleChange) {
      onVisibleChange(false, category, table_id);
    } else {
      setVisible(false);
    }
  };

  const onTextChange = (e) => {
    setSearchTxt(e.target.value);
  };

  const handleSelect = (data: Digital_Protocol_Arm_Types.Option) => {
    const selectedItem = newSelected?.find(
      (selectedItem) => selectedItem?.id === data?.id
    );
    if (selectedItem) {
      setNewSelected(newSelected.filter((item) => item.id !== selectedItem.id)); // unselect
    } else if (
      category === "Arm" ||
      (category === "Cohort" && newSelected?.length < SELECT_LIMIT)
    ) {
      setNewSelected([...newSelected, data]);
    } else {
      setNewSelected([...newSelected, data]);
      message.warning(COHORT_EXCEED_MSG);
    }
  };

  const handleUpdate = async () => {
    if (errMsg) {
      return;
    }

    if (table_id) {
      // do update for SoA table list page
      let mappingData: Digital_Protocol_Arm_Cohort_Mapping.Payload = {
        table_id,
        arm_id: [],
        cohort_id: [],
      };
      if (category === "Arm") {
        if (newSelected.length > 0) {
          const arm_id = newSelected.map((item) => item.id);
          // after the arm change, compare the existed cohorts with the cohorts scope, remove the unavailable cohorts from the selection
          const cohortsInScope: number[] = allOptions
            .filter((item) => arm_id.includes(item.id))
            .reduce(
              (accumulator, currentValue) => [
                ...accumulator,
                ...currentValue.subOptionIds,
              ],
              []
            );
          const cohort_id = selected.cohorts.filter((item) =>
            cohortsInScope.includes(item.id)
          );
          mappingData = {
            ...mappingData,
            arm_id: newSelected,
            cohort_id,
          };
        }
      } else {
        mappingData = {
          ...mappingData,
          arm_id: selected.arms,
          cohort_id: newSelected,
        };
      }

      if (mappingData.cohort_id.length > SELECT_LIMIT) {
        message.warning(COHORT_EXCEED_MSG);
        return;
      }

      setIsSaving(true);
      const isSaved = await doConfirm(mappingData);
      setIsSaving(isSaved);
      if (isSaved) {
        closePopover();
      }
    } else {
      // do update for Create SoA modal page
      if (category === "Cohort" && newSelected?.length > SELECT_LIMIT) {
        message.warning(COHORT_EXCEED_MSG);
        return;
      }
      doConfirm(newSelected);
      closePopover();
    }
  };

  useEffect(() => {
    setFilteredAllOptions(
      !searchTxt
        ? allOptions
        : allOptions.filter((item) =>
            item?.name.toLowerCase().includes(searchTxt.toLowerCase())
          )
    );
  }, [allOptions, searchTxt]);

  useEffect(() => {
    if (visible) {
      setSearchTxt("");
      setNewSelected(category === "Arm" ? selected.arms : selected.cohorts);
      setIsSaving(false);
    }
  }, [visible, selected.arms, selected.cohorts,category]);

  return (
    <Popover
      placement="bottom"
      trigger="click"
      className={styles.popover}
      overlayClassName="soa-cohort-popover"
      visible={visible}
      onVisibleChange={(v) => onVisibleChange ? onVisibleChange(v, category, table_id): setVisible(v)
      }
      content={() => (
        <div className={styles.popupRoot} onClick={(e) => e.stopPropagation()}>
          <div className={styles.body}>
            <div className={styles.input}>
              <Input
                prefix={<SearchOutlined style={{ color: "#666" }} />}
                placeholder={`Search ${category}`}
                value={searchTxt}
                onChange={onTextChange}
              />
            </div>
            {errMsg && <div className={styles.errTxt}>{errMsg}</div>}
            <div className={styles.listWrapper}>
              {filteredAllOptions.map((item) => (
                <div
                  className={`${styles.listItem} 
                  ${
                    newSelected?.some((selItem) => selItem?.id === item?.id)
                      ? styles.active
                      : ""
                  }
                  `}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect({
                      id: item.id,
                      name: item.name,
                    });
                  }}
                  key={item.id}
                >
                  <div className={styles.content}>
                    <div className={styles.name}>{item.name}</div>
                    <div className={styles.desc}>{item.description}</div>
                  </div>
                  <img src={checkIcon} className={styles.icon} alt="checked" />
                </div>
              ))}
            </div>
          </div>

          <div className={styles.footer} onClick={(e) => e.stopPropagation()}>
            <Button
              type="link"
              size="small"
              className={styles.btn}
              onClick={closePopover}
            >
              Cancel
            </Button>
            <Button
              type="link"
              size="small"
              className={`${styles.btn} ${styles.update}`}
              onClick={handleUpdate}
              loading={isSaving}
              // disabled={newSelected.length > COHORT_LIMIT}
            >
              Update
            </Button>
          </div>
        </div>
      )}
      destroyTooltipOnHide
    >
      <div className={styles.addChort} onClick={openPopover}>
        <img src={addIcon} className={styles.icon} alt={`Add ${category}`} />
      </div>
    </Popover>
  );
}
